import { useConfig } from "@/context/ConfigContext";
import { useEffect, useState } from "react";

/**
 * Hook untuk mengambil collections dari API
 * @param {Object} options - Options untuk fetch
 * @param {number|string} options.id - ID collection spesifik (optional)
 * @param {string} options.slug - Slug collection spesifik (optional)
 * @param {Object} options.params - Query parameters (optional)
 * @param {boolean} options.enabled - Enable/disable fetch (default: true)
 */
export function useCollections(options = {}) {
  const { id, slug, params = {}, enabled = true } = options;
  const { config, configData } = useConfig();
  const [collections, setCollections] = useState([]);
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    // Pastikan config sudah ready dan team_id tersedia
    if (!enabled || !configData?.team_id || !config?.api?.baseUrl) {
      return;
    }

    const fetchCollections = async () => {
      try {
        setLoading(true);
        setError(null);

        const teamId = configData.team_id;

        // Build query string dari params
        const queryParams = new URLSearchParams();
        if (params.featured !== undefined)
          queryParams.append("featured", params.featured);
        if (params.page) queryParams.append("page", params.page);
        if (params.per_page) queryParams.append("per_page", params.per_page);

        const queryString = queryParams.toString();

        // Gunakan proxy API route untuk menghindari CORS
        const collectionKey = slug || id;
        const proxyUrl = collectionKey
          ? `/api/products?team_id=${teamId}&type=collections&slug=${collectionKey}${
              queryString ? `&${queryString}` : ""
            }`
          : `/api/products?team_id=${teamId}&type=collections${
              queryString ? `&${queryString}` : ""
            }`;

        // console.log("[useCollections] Fetching collections:", {
        //   proxyUrl,
        //   teamId,
        //   id,
        //   slug,
        //   params,
        //   baseUrl: config?.api?.baseUrl,
        // });

        const response = await fetch(proxyUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          cache: "no-cache",
        });

        if (!response.ok) {
          let errorData;
          let errorText;

          try {
            errorText = await response.text();
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            errorData = {
              message: errorText || response.statusText,
              status: response.status,
            };
          }

          const apiUrl = errorData.apiUrl || "N/A";

          const errorDetails = {
            proxyEndpoint: proxyUrl,
            laravelApiUrl: apiUrl,
            status: response.status,
            statusText: response.statusText,
            teamId: teamId,
            params: params,
            apiBaseUrl: config?.api?.baseUrl,
            errorMessage:
              errorData.message ||
              errorData.error ||
              errorText ||
              response.statusText,
            errorDetail: errorData.errorDetail || errorData.errorData,
            errorData: errorData,
          };

          console.error("[useCollections] API Error:", errorDetails);

          throw new Error(
            `API Error\n` +
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
              `Proxy Endpoint: ${proxyUrl}\n` +
              `Laravel API URL: ${apiUrl}\n` +
              `Status: ${response.status} ${response.statusText}\n` +
              `Team ID: ${teamId}\n` +
              `Params: ${JSON.stringify(params)}\n` +
              `API Base URL: ${config?.api?.baseUrl}\n` +
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
              `Error Message: ${
                errorData.message ||
                errorData.error ||
                errorText ||
                response.statusText
              }\n` +
              `Error Detail: ${
                errorData.errorDetail
                  ? errorData.errorDetail
                  : JSON.stringify(errorData, null, 2)
              }`,
          );
        }

        const data = await response.json();

        // Handle response berdasarkan apakah ada id atau tidak
        if (collectionKey) {
          // Single collection
          setCollection(data.data || data);
        } else {
          // List collections
          if (data.meta || data.pagination || data.data?.meta) {
            setPagination(data.meta || data.pagination || data.data?.meta);
          }
          setCollections(data.data || data || []);
        }
      } catch (err) {
        console.error("[useCollections] Error fetching collections:", {
          message: err.message,
          stack: err.stack,
          teamId: configData?.team_id,
          params: params,
          baseUrl: config?.api?.baseUrl,
        });
        setError(err.message || "Gagal mengambil collections");
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, [
    configData?.team_id,
    config?.api?.baseUrl,
    id,
    slug,
    enabled,
    JSON.stringify(params),
  ]);

  const hasSingleCollection = !!(slug || id);

  return {
    collections: hasSingleCollection ? null : collections,
    collection: hasSingleCollection ? collection : null,
    loading,
    error,
    pagination,
  };
}
